import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Check, CreditCard, Sparkles } from 'lucide-react';
import { billingAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from './common/Button';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';

// Annual billing convention (mirrors backend config/plans.js): 10× the monthly
// price, i.e. two months free.
const YEAR_MULTIPLIER = 10;

const fmtUsd = (n) => `$${n}`;
const fmtInr = (n) => `₹${n.toLocaleString('en-IN')}`;

// Lazily inject Razorpay Checkout.js the first time it's needed.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Billing = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [catalog, setCatalog] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [interval, setInterval] = useState('month');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(''); // `${plan}:${provider}` while a checkout is starting

  const currentPlan = user?.plan || 'free';

  const load = useCallback(async () => {
    const reqs = [billingAPI.getPlans()];
    if (isAuthenticated) reqs.push(billingAPI.getSubscription());
    const [plansRes, subRes] = await Promise.all(reqs);
    if (plansRes.success) setCatalog(plansRes.data);
    else setError(plansRes.message || 'Failed to load plans');
    if (subRes?.success) setSubscription(subRes.data.subscription);
  }, [isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  // Surface the redirect result from Stripe Checkout (?status=success|cancelled).
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') setInfo('Payment received! Your plan will update within a few moments.');
    else if (status === 'cancelled') setError('Checkout was cancelled.');
    if (status) {
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const priceFor = (plan, currency) => {
    const base = plan.price?.[currency] ?? 0;
    return interval === 'year' ? base * YEAR_MULTIPLIER : base;
  };

  const startCheckout = async (planKey, provider, planLabel) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setError(''); setInfo('');
    setBusy(`${planKey}:${provider}`);
    try {
      const res = await billingAPI.checkout({ provider, plan: planKey, interval });
      if (!res.success) { setError(res.message || 'Failed to start checkout'); return; }

      if (provider === 'stripe' && res.data.url) {
        window.location.href = res.data.url; // hosted Stripe Checkout
        return;
      }

      if (provider === 'razorpay') {
        const ok = await loadRazorpayScript();
        if (!ok) { setError('Could not load Razorpay. Please try again.'); return; }
        const rzp = new window.Razorpay({
          key: res.data.keyId,
          subscription_id: res.data.subscriptionId,
          name: res.data.appName || 'Dynalink',
          description: `${planLabel} — billed ${interval === 'year' ? 'yearly' : 'monthly'}`,
          prefill: { email: res.data.prefillEmail },
          theme: { color: '#4f46e5' },
          handler: () => setInfo('Payment received! Your plan will update within a few moments.'),
        });
        rzp.on('payment.failed', () => setError('Payment failed. Please try again.'));
        rzp.open();
      }
    } catch (e) {
      setError(e.message || 'Something went wrong starting checkout');
    } finally {
      setBusy('');
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel your subscription? You keep access until the end of the current period.')) return;
    setError(''); setInfo('');
    const res = await billingAPI.cancel();
    if (res.success) { setInfo('Your subscription will end at the period end.'); load(); }
    else setError(res.message || 'Failed to cancel subscription');
  };

  // Open the Stripe Billing Portal to update card / change plan / cancel.
  const openPortal = async () => {
    setError(''); setInfo('');
    setBusy('portal');
    try {
      const res = await billingAPI.portal();
      if (res.success && res.data.url) window.location.href = res.data.url;
      else setError(res.message || 'Failed to open the billing portal');
    } catch (e) {
      setError(e.message || 'Failed to open the billing portal');
    } finally {
      setBusy('');
    }
  };

  if (!catalog) {
    return <div className="flex justify-center py-20"><LoadingSpinner /></div>;
  }

  const planCards = catalog.plans || [];
  const isLive = ['active', 'trialing', 'past_due'].includes(subscription?.status);

  return (
    <div className="max-w-5xl mx-auto">
      <header className="text-center">
        <h1 className="text-3xl font-bold tracking-tight text-ink-900">Plans &amp; Pricing</h1>
        <p className="mt-2 text-ink-500">Upgrade for higher limits. Cancel anytime.</p>
      </header>

      {info && <Alert type="success" message={info} className="mt-6" onClose={() => setInfo('')} />}
      {error && <Alert type="error" message={error} className="mt-6" onClose={() => setError('')} />}

      {!catalog.enabled && (
        <Alert
          type="warning"
          className="mt-6"
          message="Online checkout isn't configured on this server yet. Plans are shown for reference."
        />
      )}

      {/* Current subscription summary */}
      {isAuthenticated && isLive && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 bg-white p-5 shadow-soft">
          <div className="text-sm text-ink-700">
            <span className="font-semibold text-ink-900 capitalize">{subscription.plan}</span> plan ·{' '}
            <span className="capitalize">{subscription.status.replace('_', ' ')}</span>
            {subscription.cancel_at_period_end && <span className="text-amber-600"> · ends at period end</span>}
            {subscription.current_period_end && (
              <span className="text-ink-500"> · renews {new Date(subscription.current_period_end).toLocaleDateString()}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {subscription.provider === 'stripe' && (
              <Button variant="secondary" size="small" loading={busy === 'portal'} onClick={openPortal}>
                <CreditCard className="h-4 w-4" /> Manage subscription
              </Button>
            )}
            {!subscription.cancel_at_period_end && (
              <Button variant="outline" size="small" onClick={cancel}>Cancel plan</Button>
            )}
          </div>
        </div>
      )}

      {/* Billing interval toggle */}
      <div className="mt-8 flex justify-center">
        <div className="inline-flex rounded-xl border border-ink-200 bg-white p-1 shadow-soft">
          {['month', 'year'].map((iv) => (
            <button
              key={iv}
              onClick={() => setInterval(iv)}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium transition ${
                interval === iv ? 'bg-ink-900 text-white' : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              {iv === 'month' ? 'Monthly' : 'Yearly'}
              {iv === 'year' && <span className="ml-1.5 text-xs opacity-80">2 months free</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {planCards.map((plan) => {
          const isCurrent = currentPlan === plan.key;
          const avail = plan.intervals?.[interval] || {};
          const period = interval === 'year' ? '/yr' : '/mo';
          return (
            <div
              key={plan.key}
              className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-soft ${
                plan.isHighlighted ? 'border-brand-300 ring-1 ring-brand-200' : 'border-ink-200'
              }`}
            >
              {plan.isHighlighted && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                  <Sparkles className="h-3 w-3" /> Popular
                </span>
              )}

              <div className="flex items-baseline justify-between">
                <h2 className="text-lg font-bold text-ink-900">{plan.label}</h2>
                {isCurrent && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Current</span>
                )}
              </div>
              <p className="mt-1 text-sm text-ink-500">{plan.tagline}</p>

              <div className="mt-4">
                {plan.paid ? (
                  <div className="flex items-end gap-1">
                    <span className="text-3xl font-bold text-ink-900">{fmtUsd(priceFor(plan, 'usd'))}</span>
                    <span className="pb-1 text-sm text-ink-500">{period}</span>
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-ink-900">Free</div>
                )}
                {plan.paid && (
                  <p className="mt-0.5 text-xs text-ink-400">or {fmtInr(priceFor(plan, 'inr'))}{period} via Razorpay</p>
                )}
              </div>

              <ul className="mt-5 space-y-2 text-sm text-ink-700">
                <PlanFeature value={plan.quotas.maxLinks} label="links" />
                <PlanFeature value={plan.quotas.maxProfilePages} label="profile pages" />
                <PlanFeature value={plan.quotas.maxWebhooks} label="webhooks" />
              </ul>

              <div className="mt-6 space-y-2 pt-2">
                {!plan.paid ? (
                  <Button variant="outline" fullWidth disabled>{isCurrent ? 'Your plan' : 'Free forever'}</Button>
                ) : isCurrent && isLive ? (
                  <Button variant="outline" fullWidth disabled>Current plan</Button>
                ) : (
                  <>
                    {avail.stripe && (
                      <Button
                        variant="brand"
                        fullWidth
                        loading={busy === `${plan.key}:stripe`}
                        onClick={() => startCheckout(plan.key, 'stripe', plan.label)}
                      >
                        <CreditCard className="h-4 w-4" /> Pay with card (Stripe)
                      </Button>
                    )}
                    {avail.razorpay && (
                      <Button
                        variant="secondary"
                        fullWidth
                        loading={busy === `${plan.key}:razorpay`}
                        onClick={() => startCheckout(plan.key, 'razorpay', plan.label)}
                      >
                        Pay with Razorpay
                      </Button>
                    )}
                    {!avail.stripe && !avail.razorpay && (
                      <Button variant="outline" fullWidth disabled>Unavailable</Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isAuthenticated && <InvoiceHistory />}

      {!isAuthenticated && (
        <p className="mt-8 text-center text-sm text-ink-500">
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">Sign in</Link> to subscribe.
        </p>
      )}
    </div>
  );
};

// --- Invoice / receipt history -------------------------------------------

const fmtMoney = (amount, currency) =>
  `${(currency || 'usd').toUpperCase()} ${((amount || 0) / 100).toLocaleString()}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');

const InvoiceHistory = () => {
  const [invoices, setInvoices] = useState(null);

  useEffect(() => {
    let active = true;
    billingAPI
      .getInvoices({ limit: 20 })
      .then((res) => { if (active && res.success) setInvoices(res.data.invoices || []); })
      .catch(() => { if (active) setInvoices([]); });
    return () => { active = false; };
  }, []);

  // Nothing to show until there's at least one paid invoice.
  if (!invoices || invoices.length === 0) return null;

  return (
    <section className="mt-10 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-soft">
      <div className="border-b border-ink-100 bg-ink-50/60 px-5 py-3">
        <h3 className="text-sm font-semibold text-ink-900">Invoice history</h3>
        <p className="mt-0.5 text-xs text-ink-500">
          A receipt for every successful payment. Open an invoice to view the provider's receipt.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-ink-100 bg-ink-50/40 text-xs uppercase tracking-wide text-ink-500">
              <th className="px-5 py-2 font-semibold">Invoice</th>
              <th className="px-5 py-2 font-semibold">Plan</th>
              <th className="px-5 py-2 font-semibold">Issued</th>
              <th className="px-5 py-2 text-right font-semibold">Amount</th>
              <th className="px-5 py-2 text-right font-semibold">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const href = inv.hosted_url || inv.pdf_url;
              return (
                <tr key={inv.provider_invoice_id} className="border-b border-ink-50 last:border-0">
                  <td className="px-5 py-3">
                    <p className="font-mono text-xs font-semibold text-ink-800">{inv.number || inv.provider_invoice_id}</p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-400">{inv.provider}</p>
                  </td>
                  <td className="px-5 py-3 capitalize text-ink-700">
                    {inv.plan || '—'}
                    {inv.interval && <span className="ml-1 text-[10px] text-ink-400">/{inv.interval}</span>}
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-500">{fmtDate(inv.issued_at)}</td>
                  <td className="px-5 py-3 text-right font-semibold text-emerald-700">
                    {fmtMoney(inv.amount, inv.currency)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-brand-600 hover:text-brand-700">
                        View →
                      </a>
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// A quota line: `null` means unlimited.
const PlanFeature = ({ value, label }) => (
  <li className="flex items-center gap-2">
    <Check className="h-4 w-4 flex-shrink-0 text-emerald-500" />
    <span>{value === null ? 'Unlimited' : value} {label}</span>
  </li>
);

export default Billing;
